import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Row,
  Col,
  Alert,
  message,
  Skeleton,
  Modal,
  Space,
  Tabs,
  Input,
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  fetchAdsSummary,
  fetchAdsCampaigns,
  pauseAdsCampaign,
  resumeAdsCampaign,
  postCampaignNote,
  type AdsSummary,
  type Campaign,
  type AdsListParams,
} from '../../services/adsService';
import {
  AdsFilters,
  type AdsFiltersValues,
} from '../../components/ads/AdsFilters';
import { AdsTable } from '../../components/ads/AdsTable';
import { AdsDrawer } from '../../components/ads/AdsDrawer';
import { CreateCampaignModal } from '../../components/ads/CreateCampaignModal';
import AdsOpsTab from '../../components/ads/AdsOpsTab';
import { EmptyState } from '../../design-system/EmptyState';
import KPICard from '../../components/dashboard/KPICard';
import dayjs from 'dayjs';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const PAGE_SIZE = 20;

export default function AdsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState<AdsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(() =>
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(() =>
    parseInt(searchParams.get('pageSize') || String(PAGE_SIZE), 10)
  );
  const [total, setTotal] = useState(0);
  const [drawerCampaignId, setDrawerCampaignId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');

  const [filters, setFilters] = useState<AdsFiltersValues>(() => ({
    search: searchParams.get('search') || searchParams.get('q') || undefined,
    type: (searchParams.get('type') as AdsFiltersValues['type']) || undefined,
    status:
      (searchParams.get('status') as AdsFiltersValues['status']) || undefined,
    budgetState:
      (searchParams.get('budgetState') as AdsFiltersValues['budgetState']) ||
      undefined,
    sortBy:
      (searchParams.get('sortBy') as AdsFiltersValues['sortBy']) || 'newest',
    dateRange: (() => {
      const from =
        searchParams.get('dateFrom') || searchParams.get('start_at_from');
      const to = searchParams.get('dateTo') || searchParams.get('start_at_to');
      if (from && to)
        return [dayjs(from), dayjs(to)] as [dayjs.Dayjs, dayjs.Dayjs];
      return undefined;
    })(),
  }));

  const buildParams = useCallback((): AdsListParams => {
    const p: AdsListParams = {
      page,
      pageSize,
      sortBy: filters.sortBy ?? 'newest',
    };
    if (filters.search?.trim()) p.search = filters.search.trim();
    if (filters.type) p.type = filters.type;
    if (filters.status) p.status = filters.status;
    if (filters.budgetState) p.budgetState = filters.budgetState;
    if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
      p.dateFrom = filters.dateRange[0].format('YYYY-MM-DD');
      p.dateTo = filters.dateRange[1].format('YYYY-MM-DD');
    }
    return p;
  }, [page, pageSize, filters]);

  const syncUrl = useCallback(
    (params: AdsListParams) => {
      const next = new URLSearchParams();
      if (params.page && params.page > 1) next.set('page', String(params.page));
      if (params.pageSize && params.pageSize !== PAGE_SIZE)
        next.set('pageSize', String(params.pageSize));
      if (params.search) next.set('search', params.search);
      if (params.type) next.set('type', params.type);
      if (params.status) next.set('status', params.status);
      if (params.budgetState) next.set('budgetState', params.budgetState);
      if (params.sortBy && params.sortBy !== 'newest')
        next.set('sortBy', params.sortBy);
      if (params.dateFrom) next.set('dateFrom', params.dateFrom);
      if (params.dateTo) next.set('dateTo', params.dateTo);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await fetchAdsSummary();
      setSummary(res);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadCampaigns = useCallback(
    async (overrides?: { page?: number; pageSize?: number }) => {
      setLoading(true);
      setError(null);
      const params = buildParams();
      if (overrides?.page != null) params.page = overrides.page;
      if (overrides?.pageSize != null) params.pageSize = overrides.pageSize;
      try {
        const res = await fetchAdsCampaigns(params);
        setCampaigns(res.data);
        setTotal(res.meta.total);
        syncUrl(params);
      } catch (e: unknown) {
        const ax = e as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setError(
          ax.response?.data?.message ||
            ax.message ||
            'Lỗi mạng. Vui lòng thử lại.'
        );
        message.error('Không tải được danh sách chiến dịch');
        setCampaigns([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [buildParams, syncUrl]
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleSearch = () => {
    setPage(1);
    loadCampaigns({ page: 1 });
  };

  const handleReset = () => {
    setFilters({
      search: undefined,
      type: undefined,
      status: undefined,
      budgetState: undefined,
      sortBy: 'newest',
      dateRange: undefined,
    });
    setPage(1);
  };

  const handlePageChange = (p: number, size?: number) => {
    setPage(p);
    if (size != null) setPageSize(size);
    loadCampaigns(size != null ? { page: p, pageSize: size } : { page: p });
  };

  const handleRowClick = (record: Campaign) => {
    setDrawerCampaignId(record.id);
    setDrawerOpen(true);
  };

  const handleExportCsv = async () => {
    const CSV_PAGE_SIZE = 1000;
    const params = buildParams();
    params.page = 1;
    params.pageSize = CSV_PAGE_SIZE;
    try {
      const res = await fetchAdsCampaigns(params);
      if (res.meta.total > CSV_PAGE_SIZE) {
        Modal.confirm({
          title: 'Cảnh báo',
          content: `Có ${res.meta.total} chiến dịch. Chỉ xuất ${CSV_PAGE_SIZE} đầu tiên. Tiếp tục?`,
          okText: 'Xuất',
          cancelText: 'Hủy',
          onOk: () => exportCsv(res.data),
        });
      } else {
        exportCsv(res.data);
      }
    } catch (e) {
      message.error('Không thể tải dữ liệu để xuất CSV');
    }
  };

  const exportCsv = (data: Campaign[]) => {
    const headers = [
      'ID',
      'Title',
      'Type',
      'Status',
      'Merchant',
      'CPSO',
      'Spent',
      'Total',
      'Orders',
    ];
    const rows = data.map(c => [
      c.id,
      `"${(c.title || '').replace(/"/g, '""')}"`,
      c.type,
      c.status,
      `"${(c.merchantName || '').replace(/"/g, '""')}"`,
      c.costPerSuccessOrderPoints,
      c.spentPoints,
      c.totalBudgetPoints,
      c.attributedOrdersTotal ?? 0,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ads-campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(`Đã xuất ${data.length} chiến dịch`);
  };

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Ads Management</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">Quảng cáo</span>
        </nav>
      </header>

      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Quản lý chiến dịch quảng cáo, ranking CPA, ví hold/settle và chống gian
        lận
      </p>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 24 }}
        items={[
          { key: 'campaigns', label: 'Chiến dịch' },
          { key: 'ops', label: 'Vận hành & Gian lận' },
        ]}
      />

      {activeTab === 'ops' ? (
        <AdsOpsTab />
      ) : (
        <>
          <div
            style={{
              marginBottom: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalOpen(true)}
              >
                Tạo chiến dịch (Admin)
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportCsv}>
                Xuất CSV
              </Button>
            </Space>
          </div>

          {/* KPI Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              {summaryLoading ? (
                <Skeleton.Input active style={{ width: '100%', height: 100 }} />
              ) : (
                <KPICard
                  title="Chiến dịch đang chạy"
                  value={summary?.activeCampaigns ?? 0}
                  prefix={<span style={{ fontSize: 20 }}>📊</span>}
                />
              )}
            </Col>
            <Col xs={24} sm={12} lg={6}>
              {summaryLoading ? (
                <Skeleton.Input active style={{ width: '100%', height: 100 }} />
              ) : (
                <KPICard
                  title="Chi tiêu hôm nay"
                  value={summary?.totalSpendToday ?? 0}
                  suffix="điểm"
                  previousValue={summary?.totalSpend7d}
                />
              )}
            </Col>
            <Col xs={24} sm={12} lg={6}>
              {summaryLoading ? (
                <Skeleton.Input active style={{ width: '100%', height: 100 }} />
              ) : (
                <KPICard
                  title="Đơn hàng hôm nay"
                  value={summary?.attributedOrdersToday ?? 0}
                  suffix="đơn"
                />
              )}
            </Col>
            <Col xs={24} sm={12} lg={6}>
              {summaryLoading ? (
                <Skeleton.Input active style={{ width: '100%', height: 100 }} />
              ) : (
                <KPICard
                  title="Merchant chạy ads"
                  value={summary?.merchantsRunningAds ?? 0}
                  suffix="quán"
                />
              )}
            </Col>
          </Row>

          <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 24 }}>
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: '#fff',
                paddingBottom: 8,
              }}
            >
              <AdsFilters
                values={filters}
                onChange={setFilters}
                onSearch={handleSearch}
                onReset={handleReset}
                loading={loading}
              />
            </div>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                action={
                  <Button size="small" onClick={() => loadCampaigns()}>
                    Thử lại
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}

            {!error && campaigns.length === 0 && !loading && (
              <EmptyState
                description="Chưa có chiến dịch quảng cáo nào"
                onRefresh={loadCampaigns}
                onClearFilters={handleReset}
              />
            )}

            {!error && (campaigns.length > 0 || loading) && (
              <AdsTable
                data={campaigns}
                loading={loading}
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={handlePageChange}
                onRowClick={handleRowClick}
                onPause={(id, e) => {
                  e.stopPropagation();
                  Modal.confirm({
                    title: 'Tạm dừng chiến dịch',
                    content: 'Bạn có chắc muốn tạm dừng chiến dịch này?',
                    okText: 'Tạm dừng',
                    cancelText: 'Hủy',
                    onOk: async () => {
                      setCampaigns(prev =>
                        prev.map(c =>
                          c.id === id ? { ...c, status: 'PAUSED' as const } : c
                        )
                      );
                      try {
                        await pauseAdsCampaign(id);
                        message.success('Đã tạm dừng chiến dịch');
                        loadCampaigns();
                        loadSummary();
                      } catch {
                        message.error('Không thể tạm dừng');
                        loadCampaigns();
                      }
                    },
                  });
                }}
                onResume={(id, e) => {
                  e.stopPropagation();
                  const rec = campaigns.find(c => c.id === id);
                  const canResume =
                    rec && rec.remainingPoints >= rec.costPerSuccessOrderPoints;
                  if (!canResume) {
                    message.warning(
                      'Ngân sách còn lại không đủ để tiếp tục (cần >= CPSO)'
                    );
                    return;
                  }
                  Modal.confirm({
                    title: 'Tiếp tục chiến dịch',
                    content: 'Bạn có chắc muốn kích hoạt lại chiến dịch?',
                    okText: 'Tiếp tục',
                    cancelText: 'Hủy',
                    onOk: async () => {
                      setCampaigns(prev =>
                        prev.map(c =>
                          c.id === id ? { ...c, status: 'ACTIVE' as const } : c
                        )
                      );
                      try {
                        await resumeAdsCampaign(id);
                        message.success('Đã kích hoạt chiến dịch');
                        loadCampaigns();
                        loadSummary();
                      } catch {
                        message.error('Không thể tiếp tục');
                        loadCampaigns();
                      }
                    },
                  });
                }}
                onEnd={(id, e) => {
                  e.stopPropagation();
                  setDrawerCampaignId(id);
                  setDrawerOpen(true);
                }}
                onFlag={(id, e) => {
                  e.stopPropagation();
                  const rec = campaigns.find(c => c.id === id);
                  const existingNote = rec?.adminNote || '';
                  let noteValue = existingNote;
                  Modal.confirm({
                    title: existingNote
                      ? 'Sửa ghi chú / Gắn cờ'
                      : 'Gắn cờ chiến dịch',
                    content: (
                      <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        defaultValue={existingNote}
                        placeholder="Ghi chú lý do gắn cờ..."
                        onChange={ev => {
                          noteValue = ev.target.value;
                        }}
                      />
                    ),
                    okText: 'Lưu',
                    cancelText: 'Hủy',
                    onOk: async () => {
                      try {
                        await postCampaignNote(id, noteValue.trim());
                        message.success('Đã lưu ghi chú');
                        loadCampaigns();
                      } catch {
                        message.error('Không thể lưu ghi chú');
                      }
                    },
                  });
                }}
              />
            )}
          </Card>

          <AdsDrawer
            campaignId={drawerCampaignId}
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              setDrawerCampaignId(null);
            }}
            onRefresh={() => {
              loadCampaigns();
              loadSummary();
            }}
          />

          <CreateCampaignModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onCreated={() => {
              setCreateModalOpen(false);
              loadCampaigns();
              loadSummary();
            }}
          />
        </>
      )}
    </div>
  );
}
